"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import styles from "@/app/home.module.css";
import { ProductCard, type ProductCardProduct } from "./product-card";

/** A seamless, lightweight loop for the home collection. */
export function ProductMarquee({ children, label, duration = 58 }: { children: ReactNode[]; label: string; duration?: number }) {
  const [showLoopCopy, setShowLoopCopy] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const widthRef = useRef(0);
  const pausedRef = useRef(false);
  const visibleRef = useRef(false);
  const dragRef = useRef<{ startX: number; startOffset: number; moved: boolean } | null>(null);
  const suppressClickRef = useRef(false);
  const pointerFocusRef = useRef(false);
  const suppressClickFrameRef = useRef(0);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    const group = groupRef.current;
    if (!viewport || !track || !group) return;
    const motion = window.matchMedia("(prefers-reduced-motion: no-preference)");
    const desktop = window.matchMedia("(min-width: 761px) and (hover: hover) and (pointer: fine)");
    const updateLoopCopy = () => setShowLoopCopy(desktop.matches);
    updateLoopCopy();
    track.style.animation = "none";
    const measure = () => {
      const previous = widthRef.current;
      widthRef.current = group.getBoundingClientRect().width;
      offsetRef.current = previous ? offsetRef.current / previous * widthRef.current : -widthRef.current;
      // Mobile uses native horizontal scrolling. Never apply the desktop
      // marquee offset there; even a one-frame offset can make the rail jump.
      track.style.transform = motion.matches && desktop.matches
        ? `translate3d(${offsetRef.current}px,0,0)`
        : "";
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(group);
    const visibility = new IntersectionObserver(([entry]) => { visibleRef.current = entry.isIntersecting; });
    visibility.observe(viewport);
    let frame = 0;
    let last = 0;
    const tick = (time: number) => {
      if (!motion.matches || !desktop.matches) return;
      if (last && visibleRef.current && !document.hidden && !pausedRef.current && !dragRef.current && widthRef.current) {
        offsetRef.current += Math.min(time - last, 64) * widthRef.current / (duration * 1000);
        if (offsetRef.current >= 0) offsetRef.current -= widthRef.current;
        track.style.transform = `translate3d(${offsetRef.current}px,0,0)`;
      }
      last = time;
      frame = requestAnimationFrame(tick);
    };
    const startMotion = () => {
      cancelAnimationFrame(frame);
      last = 0;
      if (motion.matches && desktop.matches) frame = requestAnimationFrame(tick);
      else track.style.transform = "";
    };
    startMotion();
    desktop.addEventListener("change", startMotion);
    desktop.addEventListener("change", updateLoopCopy);
    motion.addEventListener("change", startMotion);
    return () => {
      cancelAnimationFrame(frame);
      desktop.removeEventListener("change", startMotion);
      desktop.removeEventListener("change", updateLoopCopy);
      motion.removeEventListener("change", startMotion);
      observer.disconnect();
      visibility.disconnect();
      cancelAnimationFrame(suppressClickFrameRef.current);
      track.style.animation = "";
      track.style.transform = "";
    };
  }, [children.length, duration]);

  function startDrag(event: PointerEvent<HTMLDivElement>) {
    if (event.pointerType !== "mouse" || event.button !== 0 || !window.matchMedia("(min-width: 761px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)").matches || !widthRef.current) return;
    // Product tiles contain links and quick-add controls. Let a pointer press
    // that starts on an interactive item behave like a normal click; dragging
    // remains available from the empty rail area between and around the cards.
    if ((event.target as HTMLElement).closest("a, button, input, select, textarea, [role='button']")) return;
    dragRef.current = { startX: event.clientX, startOffset: offsetRef.current, moved: false };
  }
  function moveDrag(event: PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag || !widthRef.current || !trackRef.current) return;
    const distance = event.clientX - drag.startX;
    // Require a deliberate movement before capturing the pointer. The cards
    // themselves are links, so a normal click must remain a normal click even
    // when the pointer shifts slightly between down and up.
    // A card link should win over small pointer jitter. Require a clear drag
    // gesture before taking pointer capture so ordinary clicks still navigate.
    if (!drag.moved && Math.abs(distance) < 24) return;
    if (!drag.moved) {
      drag.moved = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    offsetRef.current = ((drag.startOffset + distance) % widthRef.current + widthRef.current) % widthRef.current - widthRef.current;
    trackRef.current.style.transform = `translate3d(${offsetRef.current}px,0,0)`;
  }
  function endDrag(event: PointerEvent<HTMLDivElement>) {
    pointerFocusRef.current = false;
    if (dragRef.current?.moved) {
      suppressClickRef.current = true;
      cancelAnimationFrame(suppressClickFrameRef.current);
      // The browser dispatches click after pointerup. Keep suppression through
      // that event, then clear it if the gesture did not produce a click.
      suppressClickFrameRef.current = requestAnimationFrame(() => { suppressClickRef.current = false; });
    }
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }
  function focusTrack() {
    pausedRef.current = true;
    // Pointer focus should not move a card while its click is being dispatched.
    // Only keyboard focus needs the original cards brought back into view.
    if (pointerFocusRef.current) return;
    if (!window.matchMedia("(min-width: 761px) and (hover: hover) and (pointer: fine)").matches) return;
    // The original cards may have moved out of the viewport while looping.
    // Put them back in view when keyboard focus enters the rail.
    if (trackRef.current && widthRef.current) {
      offsetRef.current = 0;
      trackRef.current.style.transform = "translate3d(0,0,0)";
    }
  }
  if (!children.length) return null;
  return (
    <div
      ref={viewportRef}
      className={styles["marquee-viewport"]}
      role="region"
      tabIndex={0}
      aria-label={label}
      onPointerEnter={(event) => { if (event.pointerType === "mouse") pausedRef.current = true; }}
      onPointerLeave={(event) => { if (event.pointerType === "mouse") pausedRef.current = false; }}
      onFocusCapture={focusTrack}
      onKeyDownCapture={() => { pointerFocusRef.current = false; }}
      onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) pausedRef.current = false; }}
      onPointerDownCapture={() => { pointerFocusRef.current = true; }}
      onPointerDown={startDrag}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDragStart={(event) => event.preventDefault()}
      onClickCapture={(event) => {
        if (!suppressClickRef.current) return;
        // A drag should suppress its synthetic mouse click, never a keyboard
        // activation that happens immediately afterwards.
        if (event.detail === 0) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClickRef.current = false;
        cancelAnimationFrame(suppressClickFrameRef.current);
      }}
    >
      <div ref={trackRef} className={styles["marquee-track"]} style={{ "--marquee-duration": `${duration}s` } as CSSProperties}>
        <div ref={groupRef} className={styles["marquee-group"]}>{children}</div>
        {/* Add the loop copy only on desktop. Mobile scrolls the first group natively. */}
        {/* Reuse the original RSC children so the page does not serialize a
            second copy of every product card for mobile, where this loop is
            never shown. Keep the desktop-only visual copy out of focus. */}
        {showLoopCopy && <div className={styles["marquee-group"]} aria-hidden="true" inert>{children}</div>}
      </div>
    </div>
  );
}

const PAGE_SIZE = 3;

export function ProductCarousel({ products, label = "بیشتر برای دیدن", homeTiles = false, hasMore = false }: { products: ProductCardProduct[]; label?: string; homeTiles?: boolean; hasMore?: boolean }) {
  const [visibleProducts, setVisibleProducts] = useState(products);
  const [hasMoreProducts, setHasMoreProducts] = useState(hasMore);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingRef = useRef(false);
  const pageRef = useRef(0);
  const pages = useMemo(() => {
    const result: ProductCardProduct[][] = [];
    for (let index = 0; index < visibleProducts.length; index += PAGE_SIZE) {
      result.push(visibleProducts.slice(index, index + PAGE_SIZE));
    }
    return result;
  }, [visibleProducts]);
  const [page, setPage] = useState(0);
  const [motionAllowed, setMotionAllowed] = useState(false);
  const pausedRef = useRef(false);

  const loadNextPage = useCallback(async () => {
    const lastProduct = visibleProducts.at(-1);
    if (!hasMoreProducts || !lastProduct || loadingRef.current) return false;
    loadingRef.current = true;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams({ afterDate: lastProduct.created_at, afterId: lastProduct.id });
      const response = await fetch(`/api/home-products?${params.toString()}`);
      if (!response.ok) return false;
      const data: unknown = await response.json();
      if (!data || typeof data !== "object" || !("products" in data) || !Array.isArray(data.products)) return false;
      const nextProducts = (data.products as ProductCardProduct[]).filter((product) =>
        product && typeof product.id === "string" && !visibleProducts.some((visible) => visible.id === product.id),
      );
      setVisibleProducts((current) => [...current, ...nextProducts]);
      setHasMoreProducts("hasMore" in data && data.hasMore === true);
      return nextProducts.length > 0;
    } catch {
      return false;
    } finally {
      loadingRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMoreProducts, visibleProducts]);

  const move = useCallback(async (direction: -1 | 1) => {
    const current = pageRef.current;
    let next = current;
    if (direction < 0) next = (current - 1 + pages.length) % pages.length;
    else if (current < pages.length - 1) next = current + 1;
    else if (hasMoreProducts) {
      if (await loadNextPage()) next = current + 1;
    } else next = 0;
    pageRef.current = next;
    setPage(next);
  }, [hasMoreProducts, loadNextPage, pages.length]);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: no-preference)");
    const update = () => setMotionAllowed(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (pages.length < 2 || !motionAllowed) return;
    const timer = window.setInterval(
      () => { if (!pausedRef.current) void move(1); },
      14_000,
    );
    return () => window.clearInterval(timer);
  }, [motionAllowed, move, pages.length]);

  if (!pages.length) return null;
  return (
    <section
      className={styles["home-carousel-shell"]}
      aria-label={label}
      aria-roledescription="چرخانهٔ محصولات"
      onPointerEnter={(event) => { if (event.pointerType === "mouse") pausedRef.current = true; }}
      onPointerLeave={(event) => { if (event.pointerType === "mouse") pausedRef.current = false; }}
      onFocusCapture={() => { pausedRef.current = true; }}
      onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) pausedRef.current = false; }}
    >
      {pages.length > 1 && (
        <div className={styles["home-carousel-controls"]}>
          <button type="button" onClick={() => move(-1)} aria-label="سه محصول قبلی">
            <ChevronRight size={19} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => move(1)} aria-label="سه محصول بعدی">
            <ChevronLeft size={19} aria-hidden="true" />
          </button>
        </div>
      )}
      <div
        className={styles["home-carousel"]}
        role="group"
        aria-label={`نمایش ${label}`}
        aria-live="off"
      >
        {pages[page].map((product, index) => (
          <div className={styles["home-carousel-item"]} key={`${page}-${product.id}-${index}`}>
            {homeTiles ? <div className={styles.product}><ProductCard product={product} hoverImage imageSizes="(max-width: 760px) min(42vw, 200px), 330px" /></div> : <ProductCard product={product} />}
          </div>
        ))}
      </div>
      <div className={styles["home-carousel-mobile"]} role="group" aria-label={`محصولات ${label}`} aria-busy={loadingMore}>
        {visibleProducts.map((product) => (
          <div className={styles["home-carousel-mobile-item"]} key={product.id}>
            {homeTiles ? <div className={styles.product}><ProductCard product={product} hoverImage imageSizes="(max-width: 760px) min(42vw, 200px), 330px" /></div> : <ProductCard product={product} />}
          </div>
        ))}
        {hasMoreProducts && (
          <button className={`${styles["home-carousel-mobile-more"]} ${styles["home-carousel-mobile-load-more"]}`} type="button" onClick={() => { void loadNextPage(); }} disabled={loadingMore}>
            {loadingMore ? "در حال بارگذاری…" : "دیدن محصولات بیشتر"}
          </button>
        )}
        {(hasMoreProducts || visibleProducts.length > 12) && (
          <Link className={styles["home-carousel-mobile-more"]} href="/products">
            دیدن همهٔ محصولات
          </Link>
        )}
      </div>
    </section>
  );
}
